import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from './Header';
import Sidebar from './Sidebar';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { currentUser, changePassword, changeEmployeePassword, users } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('password');
  const [loading, setLoading] = useState(false);
  
  // Password change form - FIXED: Remove currentPassword since we're not using it
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Employee password reset form (for owners)
  const [employeeResetForm, setEmployeeResetForm] = useState({
    employeeId: '',
    newPassword: '',
    confirmPassword: ''
  });

  // App settings
  const [appSettings, setAppSettings] = useState({
    theme: localStorage.getItem('theme') || 'light',
    notifications: localStorage.getItem('notifications') !== 'false',
    emailNotifications: localStorage.getItem('emailNotifications') !== 'false',
    language: localStorage.getItem('language') || 'en'
  });

  // Get menu items based on user role
  const getMenuItems = () => {
    if (currentUser?.role === 'owner') {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'Projects', path: '/dashboard/projects', icon: 'briefcase' },
        { name: 'Employees', path: '/dashboard/employees', icon: 'users' },
        { name: 'Attendance', path: '/dashboard/attendance', icon: 'calendar-check' },
        { name: 'Reports', path: '/dashboard/reports', icon: 'chart-bar' },
      ];
    } else if (currentUser?.role === 'project_manager') {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'My Projects', path: '/dashboard/projects', icon: 'briefcase' },
        { name: 'Tasks', path: '/dashboard/tasks', icon: 'check-square' },
        { name: 'Team Analytics', path: '/dashboard/team-analytics', icon: 'chart-line' },
        { name: 'Messages', path: '/dashboard/messages', icon: 'message-square' },
      ];
    } else if (currentUser?.role === 'hr') {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'Employees', path: '/dashboard/employees', icon: 'users' },
        { name: 'Attendance', path: '/dashboard/attendance', icon: 'calendar-check' },
        { name: 'Leave Requests', path: '/dashboard/leave', icon: 'calendar-times' },
      ];
    } else {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'My Tasks', path: '/dashboard/tasks', icon: 'tasks' },
        { name: 'Attendance', path: '/dashboard/attendance', icon: 'calendar-check' },
        { name: 'Leave', path: '/dashboard/leave', icon: 'calendar-times' },
      ];
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEmployeeResetChange = (e) => {
    const { name, value } = e.target;
    setEmployeeResetForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setAppSettings(prev => ({ ...prev, [name]: newValue }));
    localStorage.setItem(name, newValue);
    
    // Apply theme immediately
    if (name === 'theme') {
      document.documentElement.setAttribute('data-theme', newValue);
    }
  };

  // FIXED: Enhanced password change function with better error handling
  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
        alert('Please fill in all password fields');
        return;
      }

      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        alert('New passwords do not match');
        return;
      }

      if (passwordForm.newPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
      }

      console.log('Attempting to change password for user:', currentUser?.id);
      console.log('New password length:', passwordForm.newPassword.length);

      // Call the changePassword function
      const result = await changePassword(passwordForm.newPassword);
      
      console.log('Change password result:', result);

      if (result && result.success) {
        alert('Password changed successfully');
        // FIXED: Reset form properly
        setPasswordForm({ newPassword: '', confirmPassword: '' });
      } else {
        alert(result?.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      alert(`Failed to change password: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const submitEmployeePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (employeeResetForm.newPassword !== employeeResetForm.confirmPassword) {
        alert('New passwords do not match');
        return;
      }

      if (employeeResetForm.newPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
      }

      const result = await changeEmployeePassword(employeeResetForm.employeeId, employeeResetForm.newPassword);
      
      if (result.success) {
        alert(result.message);
        setEmployeeResetForm({ employeeId: '', newPassword: '', confirmPassword: '' });
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Employee password reset error:', error);
      alert('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const employees = users.filter(u => u.role !== 'owner');

  return (
    <div className="dashboard-layout">
      <Sidebar 
        menuItems={getMenuItems()}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="dashboard-main">
        <Header 
          title="Settings"
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="dashboard-body">
          <div className="settings-page">
            <div className="settings-container">
              <div className="settings-header">
                <h2>Account Settings</h2>
                <p>Manage your account preferences and security settings</p>
              </div>

              {/* Settings Tabs */}
              <div className="settings-tabs">
                <button 
                  className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
                  onClick={() => setActiveTab('password')}
                >
                  <i className="fas fa-lock"></i>
                  Password
                </button>
                {currentUser?.role === 'owner' && (
                  <button 
                    className={`settings-tab ${activeTab === 'employee-reset' ? 'active' : ''}`}
                    onClick={() => setActiveTab('employee-reset')}
                  >
                    <i className="fas fa-users-cog"></i>
                    Employee Passwords
                  </button>
                )}
                <button 
                  className={`settings-tab ${activeTab === 'preferences' ? 'active' : ''}`}
                  onClick={() => setActiveTab('preferences')}
                >
                  <i className="fas fa-cog"></i>
                  Preferences
                </button>
              </div>

              {/* Settings Content */}
              <div className="settings-content">
                {/* Password Change Tab */}
                {activeTab === 'password' && (
                  <div className="settings-section">
                    <div className="section-header">
                      <h3>Change Password</h3>
                      <p>Update your account password for better security.</p>
                    </div>
                    
                    <form onSubmit={submitPasswordChange} className="settings-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">New Password</label>
                          <input
                            type="password"
                            name="newPassword"
                            value={passwordForm.newPassword}
                            onChange={handlePasswordChange}
                            className="form-control"
                            minLength="6"
                            required
                            placeholder="Enter your new password"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Confirm New Password</label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={passwordForm.confirmPassword}
                            onChange={handlePasswordChange}
                            className="form-control"
                            minLength="6"
                            required
                            placeholder="Confirm your new password"
                          />
                        </div>
                      </div>

                      <div className="password-requirements">
                        <h5>Password Requirements:</h5>
                        <ul>
                          <li className={passwordForm.newPassword.length >= 6 ? 'valid' : 'invalid'}>
                            At least 6 characters long
                          </li>
                          <li className={passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.newPassword ? 'valid' : 'invalid'}>
                            Passwords match
                          </li>
                        </ul>
                      </div>

                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Changing...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-key"></i>
                            Change Password
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}

                {/* Employee Password Reset Tab (Owner Only) */}
                {activeTab === 'employee-reset' && currentUser?.role === 'owner' && (
                  <div className="settings-section">
                    <div className="section-header">
                      <h3>Reset Employee Password</h3>
                      <p>Reset any employee's password. They will be notified of the change.</p>
                    </div>
                    
                    <form onSubmit={submitEmployeePasswordReset} className="settings-form">
                      <div className="form-group">
                        <label className="form-label">Select Employee</label>
                        <select
                          name="employeeId"
                          value={employeeResetForm.employeeId}
                          onChange={handleEmployeeResetChange}
                          className="form-control"
                          required
                        >
                          <option value="">Choose an employee...</option>
                          {employees.map(employee => (
                            <option key={employee.id} value={employee.id}>
                              {employee.name} - {employee.role.replace('_', ' ')} ({employee.email})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">New Password</label>
                          <input
                            type="password"
                            name="newPassword"
                            value={employeeResetForm.newPassword}
                            onChange={handleEmployeeResetChange}
                            className="form-control"
                            minLength="6"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Confirm New Password</label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={employeeResetForm.confirmPassword}
                            onChange={handleEmployeeResetChange}
                            className="form-control"
                            minLength="6"
                            required
                          />
                        </div>
                      </div>

                      <button type="submit" className="btn btn-warning" disabled={loading || !employeeResetForm.employeeId}>
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Resetting...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-user-lock"></i>
                            Reset Employee Password
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}

                {/* Preferences Tab */}
                {activeTab === 'preferences' && (
                  <div className="settings-section">
                    <div className="section-header">
                      <h3>Application Preferences</h3>
                      <p>Customize your application experience.</p>
                    </div>
                    
                    <div className="preferences-grid">
                      <div className="preference-group">
                        <h4>Appearance</h4>
                        <div className="form-group">
                          <label className="form-label">Theme</label>
                          <select
                            name="theme"
                            value={appSettings.theme}
                            onChange={handleSettingsChange}
                            className="form-control"
                          >
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">Language</label>
                          <select
                            name="language"
                            value={appSettings.language}
                            onChange={handleSettingsChange}
                            className="form-control"
                          >
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                          </select>
                        </div>
                      </div>

                      <div className="preference-group">
                        <h4>Notifications</h4>
                        <div className="checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              name="notifications"
                              checked={appSettings.notifications}
                              onChange={handleSettingsChange}
                            />
                            <span className="checkmark"></span>
                            Show in-app notifications
                          </label>
                        </div>
                        
                        <div className="checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              name="emailNotifications"
                              checked={appSettings.emailNotifications}
                              onChange={handleSettingsChange}
                            />
                            <span className="checkmark"></span>
                            Send email notifications
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="settings-info">
                      <div className="info-card">
                        <h4>Account Information</h4>
                        <div className="info-row">
                          <span>Account Created:</span>
                          <span>{new Date(currentUser?.joinDate).toLocaleDateString()}</span>
                        </div>
                        <div className="info-row">
                          <span>Last Login:</span>
                          <span>Today</span>
                        </div>
                        <div className="info-row">
                          <span>Account Status:</span>
                          <span className="status-active">
                            {(currentUser?.status === 'active' || !currentUser?.status) ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings Footer */}
              <div className="settings-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
